import {
  LocalAudioTrack,
  RoomEvent,
  Track,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrackPublication,
  type Room,
} from 'livekit-client'
import { useLivekitStore } from '../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useVoiceStore } from '../stores/voiceStore'
import { registerAuxAudioContext } from '../components/voice/audioContext'

/**
 * Bridge between a user's active audio-channel Room and active DM Room.
 *
 * When both rooms have `room.state === 'connected'` AND the user has
 * mic publishing active in BOTH rooms, this service:
 *   - publishes channel peer audio into the DM room (as an aux track),
 *   - publishes DM peer audio into the channel room (as a separate aux),
 * so the two rooms can hear each other through the user's client.
 *
 * Bridge tears down when:
 *   - either room becomes null or disconnected
 *   - either mic transitions to off (mute or unpublish)
 *   - either room enters 'reconnecting' (clean rebuild after reconnect)
 *
 * Privacy: the bridge does NOT establish itself by default. The
 * privacy gate (#60 in VOICE_CRITICAL_INVARIANTS.md, MR-1) auto-mutes
 * the channel mic on combined-state enter, which means "both mics ON"
 * is false initially — bridge stays OFF. The user explicitly un-mutes
 * channel mic to opt IN.
 *
 * Per VOICE_CRITICAL_INVARIANTS.md #61 — do not remove the
 * both-mics-ON gating without an ADR.
 *
 * Audio graph (per direction):
 *
 *   sourceRoom peer audio tracks
 *     → MediaStreamAudioSourceNode (one per peer)
 *     → GainNode (mixer, gain=1.0)
 *     → MediaStreamAudioDestinationNode
 *       → stream.getAudioTracks()[0]
 *         → new LocalAudioTrack(...)
 *           → destRoom.publishTrack({ source: Unknown, name })
 *
 * Sources attach on RoomEvent.TrackSubscribed, detach on
 * TrackUnsubscribed. Existing peers are walked once on enable().
 *
 * NO denoising / AGC / AEC applied — peer audio is already processed
 * upstream.
 *
 * Singleton (one user, one client, one bridge). Init from VoiceManager
 * on mount; dispose on unmount.
 */

// Epoch counter for race-guard. Incremented on every enable/disable
// call; async work checks its captured epoch before applying.
let epoch = 0

interface BridgeDirection {
  // Source room: where we pull peer audio FROM.
  sourceRoom: Room
  // Destination room: where we publish the mixed aux track TO.
  destRoom: Room
  ctx: AudioContext
  destination: MediaStreamAudioDestinationNode
  mixerGain: GainNode
  // Per-track source nodes, keyed by RemoteAudioTrack reference (so we
  // can disconnect/remove on TrackUnsubscribed).
  sources: Map<RemoteAudioTrack, MediaStreamAudioSourceNode>
  // Published aux track on destRoom. Stored so we can unpublishTrack
  // on tear-down.
  publishedTrack: LocalAudioTrack
  // RoomEvent listener disposers (TrackSubscribed/Unsubscribed +
  // mic-state listeners on both rooms).
  listeners: Array<() => void>
}

let active: {
  channelRoom: Room
  dmRoom: Room
  // DM peer audio → published into channel room.
  dmToChannel: BridgeDirection | null
  // Channel peer audio → published into DM room.
  channelToDm: BridgeDirection | null
} | null = null

// Unsubscribers for store listeners. Cleared on dispose.
const unsubscribers: Array<() => void> = []

function shouldBeActive(channelRoom: Room | null, dmRoom: Room | null): boolean {
  if (!channelRoom || !dmRoom) return false
  if (channelRoom.state !== 'connected') return false
  if (dmRoom.state !== 'connected') return false
  if (!channelRoom.localParticipant.isMicrophoneEnabled) return false
  if (!dmRoom.localParticipant.isMicrophoneEnabled) return false
  return true
}

// Lazy 48 kHz context, same pattern as agcOnly.ts. Reused across
// enable/disable cycles — destroying and recreating the ctx on every
// toggle is expensive and unnecessary (the graph nodes are cheap, the
// ctx itself isn't).
let auxCtx: AudioContext | null = null
function getOrMakeAuxContext(): AudioContext | null {
  if (auxCtx && auxCtx.state !== 'closed') return auxCtx
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new AudioCtx({ sampleRate: 48000 })
    if (ctx.sampleRate !== 48000) {
      console.error('[dm-channel-bridge] aux AudioContext sampleRate !== 48000', {
        sampleRate: ctx.sampleRate,
      })
      try { ctx.close() } catch {}
      return null
    }
    // Register so the gesture unlock (#18) + health-check resume tick
    // (#53) revive it if it suspends.
    registerAuxAudioContext(ctx)
    auxCtx = ctx
    return ctx
  } catch (err) {
    console.error('[dm-channel-bridge] failed to create aux AudioContext', err)
    return null
  }
}

async function buildDirection(args: {
  sourceRoom: Room
  destRoom: Room
  ctx: AudioContext
  trackName: string
  myEpoch: number
}): Promise<BridgeDirection | null> {
  const { sourceRoom, destRoom, ctx, trackName, myEpoch } = args

  // 1. Mixer + destination.
  const mixerGain = ctx.createGain()
  mixerGain.gain.value = 1.0
  const destination = ctx.createMediaStreamDestination()
  mixerGain.connect(destination)

  const sources = new Map<RemoteAudioTrack, MediaStreamAudioSourceNode>()

  function attachTrack(track: RemoteAudioTrack): void {
    if (epoch !== myEpoch) return
    if (sources.has(track)) return
    if (track.kind !== Track.Kind.Audio) return
    if (!track.mediaStream) return
    try {
      const src = ctx.createMediaStreamSource(track.mediaStream)
      src.connect(mixerGain)
      sources.set(track, src)
      console.info('[dm-channel-bridge] peer added', {
        direction: trackName,
        identity: track.sid,
      })
    } catch (err) {
      console.warn('[dm-channel-bridge] attachTrack failed', { trackName, err })
    }
  }

  function detachTrack(track: RemoteAudioTrack): void {
    const src = sources.get(track)
    if (!src) return
    try { src.disconnect() } catch {}
    sources.delete(track)
    console.info('[dm-channel-bridge] peer removed', {
      direction: trackName,
      identity: track.sid,
    })
  }

  // 2. Attach already-subscribed tracks.
  sourceRoom.remoteParticipants.forEach((p: RemoteParticipant) => {
    p.trackPublications.forEach((pub) => {
      if (pub.kind !== Track.Kind.Audio) return
      const track = (pub as RemoteTrackPublication).track as RemoteAudioTrack | undefined
      if (track) attachTrack(track)
    })
  })

  // 3. Subscribe to future track events.
  const onSub = (track: unknown): void => {
    const t = track as RemoteAudioTrack
    if (t && t.kind === Track.Kind.Audio) attachTrack(t)
  }
  const onUnsub = (track: unknown): void => {
    const t = track as RemoteAudioTrack
    if (t && t.kind === Track.Kind.Audio) detachTrack(t)
  }
  sourceRoom.on(RoomEvent.TrackSubscribed, onSub)
  sourceRoom.on(RoomEvent.TrackUnsubscribed, onUnsub)
  const listeners: Array<() => void> = [
    () => { sourceRoom.off(RoomEvent.TrackSubscribed, onSub) },
    () => { sourceRoom.off(RoomEvent.TrackUnsubscribed, onUnsub) },
  ]

  // 4. Publish destination stream as aux track on destRoom.
  const mediaTrack = destination.stream.getAudioTracks()[0]
  if (!mediaTrack) {
    console.error('[dm-channel-bridge] destination stream has no audio track', { trackName })
    listeners.forEach((d) => d())
    return null
  }
  const publishedTrack = new LocalAudioTrack(mediaTrack, undefined, /* userProvidedTrack */ true)
  try {
    await destRoom.localParticipant.publishTrack(publishedTrack, {
      source: Track.Source.Unknown,
      name: trackName,
    })
  } catch (err) {
    console.error('[dm-channel-bridge] publishTrack failed', { trackName, err })
    listeners.forEach((d) => d())
    try { mediaTrack.stop() } catch {}
    return null
  }

  // Post-await race-guard: if a disable() happened during publishTrack,
  // tear down what we just built.
  if (epoch !== myEpoch) {
    console.info('[dm-channel-bridge] race: epoch advanced during publishTrack — cleaning up', {
      trackName,
    })
    try { destRoom.localParticipant.unpublishTrack(publishedTrack) } catch {}
    listeners.forEach((d) => d())
    try { mediaTrack.stop() } catch {}
    return null
  }

  return {
    sourceRoom,
    destRoom,
    ctx,
    destination,
    mixerGain,
    sources,
    publishedTrack,
    listeners,
  }
}

function teardownDirection(dir: BridgeDirection): void {
  dir.listeners.forEach((d) => { try { d() } catch {} })
  dir.sources.forEach((src) => { try { src.disconnect() } catch {} })
  dir.sources.clear()
  try { dir.mixerGain.disconnect() } catch {}
  try { dir.destRoom.localParticipant.unpublishTrack(dir.publishedTrack) } catch {}
  try { dir.publishedTrack.stop() } catch {}
  try { dir.destination.stream.getTracks().forEach((t) => t.stop()) } catch {}
}

async function enable(channelRoom: Room, dmRoom: Room): Promise<void> {
  epoch += 1
  const myEpoch = epoch

  if (active) disable('reactivate')

  const ctx = getOrMakeAuxContext()
  if (!ctx) {
    console.error('[dm-channel-bridge] aux ctx unavailable — bridge skipped')
    return
  }

  // Build both directions in parallel.
  const [dmToChannel, channelToDm] = await Promise.all([
    buildDirection({
      sourceRoom: dmRoom,
      destRoom: channelRoom,
      ctx,
      trackName: 'bridge-dm',
      myEpoch,
    }),
    buildDirection({
      sourceRoom: channelRoom,
      destRoom: dmRoom,
      ctx,
      trackName: 'bridge-channel',
      myEpoch,
    }),
  ])

  if (epoch !== myEpoch) {
    // Disable came in mid-build; whatever buildDirection returned
    // (success or null) — clean up.
    if (dmToChannel) teardownDirection(dmToChannel)
    if (channelToDm) teardownDirection(channelToDm)
    return
  }

  if (!dmToChannel || !channelToDm) {
    // Partial failure — clean up whichever succeeded.
    if (dmToChannel) teardownDirection(dmToChannel)
    if (channelToDm) teardownDirection(channelToDm)
    console.error('[dm-channel-bridge] enable failed (one direction build returned null)')
    return
  }

  // Mic-state listeners on both rooms. A mute / unmute / publish /
  // unpublish event triggers re-evaluate() which decides to disable
  // (if mics no longer both-on) or no-op (if they remain both-on).
  // Zustand store subscribe alone doesn't catch all of these — e.g.
  // a track.mute() may not propagate to the Zustand state. Catch them
  // directly off the Room.
  const onMicChange = (): void => evaluate()
  channelRoom.on(RoomEvent.TrackMuted, onMicChange)
  channelRoom.on(RoomEvent.TrackUnmuted, onMicChange)
  channelRoom.on(RoomEvent.LocalTrackPublished, onMicChange)
  channelRoom.on(RoomEvent.LocalTrackUnpublished, onMicChange)
  dmRoom.on(RoomEvent.TrackMuted, onMicChange)
  dmRoom.on(RoomEvent.TrackUnmuted, onMicChange)
  dmRoom.on(RoomEvent.LocalTrackPublished, onMicChange)
  dmRoom.on(RoomEvent.LocalTrackUnpublished, onMicChange)
  const micListenerDisposers: Array<() => void> = [
    () => { channelRoom.off(RoomEvent.TrackMuted, onMicChange) },
    () => { channelRoom.off(RoomEvent.TrackUnmuted, onMicChange) },
    () => { channelRoom.off(RoomEvent.LocalTrackPublished, onMicChange) },
    () => { channelRoom.off(RoomEvent.LocalTrackUnpublished, onMicChange) },
    () => { dmRoom.off(RoomEvent.TrackMuted, onMicChange) },
    () => { dmRoom.off(RoomEvent.TrackUnmuted, onMicChange) },
    () => { dmRoom.off(RoomEvent.LocalTrackPublished, onMicChange) },
    () => { dmRoom.off(RoomEvent.LocalTrackUnpublished, onMicChange) },
  ]
  // Attach to dmToChannel.listeners so teardownDirection cleans up
  // for free. (We could split symmetric listeners across both directions,
  // but stashing them on one is simpler and the lifetimes are identical.)
  dmToChannel.listeners.push(...micListenerDisposers)

  active = { channelRoom, dmRoom, dmToChannel, channelToDm }
  console.info('[dm-channel-bridge] enable', {
    channelRoom: channelRoom.name,
    dmRoom: dmRoom.name,
    peers: {
      channel: channelRoom.remoteParticipants.size,
      dm: dmRoom.remoteParticipants.size,
    },
  })
}

function disable(reason?: string): void {
  epoch += 1
  if (!active) return
  const snapshot = active
  active = null
  if (snapshot.dmToChannel) teardownDirection(snapshot.dmToChannel)
  if (snapshot.channelToDm) teardownDirection(snapshot.channelToDm)
  console.info('[dm-channel-bridge] disable', { reason })
}

function evaluate(): void {
  const channelRoom = useLivekitStore.getState().room
  const dmRoom = useDmCallStore.getState().room
  if (shouldBeActive(channelRoom, dmRoom)) {
    if (!active || active.channelRoom !== channelRoom || active.dmRoom !== dmRoom) {
      // Fire-and-log: enable is async (publishTrack) but the lifecycle
      // continues to be driven by store events. Any race during enable
      // is caught by the epoch guard.
      enable(channelRoom!, dmRoom!).catch((err) => {
        console.error('[dm-channel-bridge] enable threw', err)
      })
    }
  } else {
    if (active) disable('state-changed')
  }
}

export function initDmChannelBridge(): void {
  if (unsubscribers.length > 0) {
    // Already initialised — likely HMR reload. Bail to avoid duplicate
    // listeners.
    return
  }
  unsubscribers.push(useLivekitStore.subscribe(evaluate))
  unsubscribers.push(useDmCallStore.subscribe(evaluate))
  // Aux playback runs on EVERY client in either room, regardless of
  // whether this client is itself bridging — peers receive aux tracks
  // from the bridger and need to attach them to <audio> elements.
  // LiveKitBackgroundAudio only handles Track.Source.Microphone, so
  // Track.Source.Unknown bridge tracks would otherwise be subscribed
  // but silent. See `installAuxPlayback` below.
  unsubscribers.push(installAuxPlayback(() => useLivekitStore.getState().room))
  unsubscribers.push(installAuxPlayback(() => useDmCallStore.getState().room))
  // React to masterVolume changes by updating every playing aux element.
  unsubscribers.push(useVoiceStore.subscribe(applyMasterVolumeToAuxElements))
  // Initial evaluation in case both rooms were already up at mount.
  evaluate()
}

export function disposeDmChannelBridge(): void {
  while (unsubscribers.length > 0) {
    const fn = unsubscribers.pop()
    try { fn?.() } catch {}
  }
  if (active) disable('dispose')
  // Tear down any aux audio elements still attached.
  playingAuxTracks.forEach((_el, track) => detachAuxAudio(track))
}

// ─────────────────────────────────────────────────────────────────────
// Aux track PLAYBACK (receiver side).
//
// LiveKitBackgroundAudio only attaches tracks with Track.Source ===
// Microphone to its Web Audio graph. The bridge publishes aux tracks
// with Track.Source.Unknown (LiveKit limits Microphone to one per
// participant), so without this service the bridge audio is received
// by peers but never played — exactly the bug reported 2026-05-26:
// "I unmuted channel during DM but the other channel members never
// heard my DM peer".
//
// We attach incoming bridge-* aux tracks (filtered by track name) to
// raw <audio> elements appended to document.body. masterVolume is
// applied via element.volume; per-peer volume + the existing limiter
// /compressor chain do NOT apply to bridge audio in v1. Reasoning:
// bridge audio has already been AGC'd at the original publisher's
// sendAgc stage, so it arrives at a moderate level, and bridge is
// itself an opt-in feature — users who don't like the loudness can
// re-mute channel mic to disable the bridge altogether.

const playingAuxTracks = new Map<RemoteAudioTrack, HTMLAudioElement>()

function isBridgeAuxName(name: string | undefined): boolean {
  return name === 'bridge-dm' || name === 'bridge-channel'
}

function applyMasterVolumeToAuxElements(): void {
  const vol = Math.max(0, Math.min(1, useVoiceStore.getState().masterVolume))
  playingAuxTracks.forEach((el) => { el.volume = vol })
}

function attachAuxAudio(track: RemoteAudioTrack, pub: RemoteTrackPublication): void {
  if (!isBridgeAuxName(pub.trackName)) return
  if (playingAuxTracks.has(track)) return
  if (!track.mediaStream) {
    console.warn('[dm-channel-bridge] aux track has no mediaStream — cannot attach', {
      name: pub.trackName,
    })
    return
  }
  const el = document.createElement('audio')
  el.autoplay = true
  el.muted = false
  el.volume = Math.max(0, Math.min(1, useVoiceStore.getState().masterVolume))
  el.setAttribute('data-bridge-aux', pub.trackName)
  el.style.display = 'none'
  el.srcObject = track.mediaStream
  document.body.appendChild(el)
  playingAuxTracks.set(track, el)
  el.play().catch((err) => {
    console.warn('[dm-channel-bridge] aux audio play() failed', { name: pub.trackName, err })
  })
  console.info('[dm-channel-bridge] aux playback attach', {
    name: pub.trackName,
    sid: track.sid,
  })
}

function detachAuxAudio(track: RemoteAudioTrack): void {
  const el = playingAuxTracks.get(track)
  if (!el) return
  try { el.pause() } catch {}
  try { el.srcObject = null } catch {}
  try { el.remove() } catch {}
  playingAuxTracks.delete(track)
  console.info('[dm-channel-bridge] aux playback detach', { sid: track.sid })
}

/**
 * Install a per-room aux-track playback listener. The room reference
 * may change over time (join channel, leave channel, etc.), so the
 * function takes a getter and subscribes to the corresponding store
 * itself — when the room transitions to null we tear listeners down,
 * and when it transitions to a new Room we install fresh ones.
 *
 * Returns a single disposer that's safe to call on dispose.
 */
function installAuxPlayback(getRoom: () => Room | null): () => void {
  let installedRoom: Room | null = null
  let installedDisposers: Array<() => void> = []

  const teardown = (): void => {
    installedDisposers.forEach((d) => { try { d() } catch {} })
    installedDisposers = []
    installedRoom = null
  }

  const reconcile = (): void => {
    const next = getRoom()
    if (next === installedRoom) return
    teardown()
    if (!next) return
    installedRoom = next

    // Walk existing peers — covers the case where we joined a room
    // that already has bridgers active.
    next.remoteParticipants.forEach((p: RemoteParticipant) => {
      p.trackPublications.forEach((pub) => {
        if (pub.kind !== Track.Kind.Audio) return
        const track = (pub as RemoteTrackPublication).track as RemoteAudioTrack | undefined
        if (track) attachAuxAudio(track, pub as RemoteTrackPublication)
      })
    })

    const onSub = (track: unknown, pub: unknown): void => {
      const t = track as RemoteAudioTrack
      const p = pub as RemoteTrackPublication
      if (t && t.kind === Track.Kind.Audio) attachAuxAudio(t, p)
    }
    const onUnsub = (track: unknown): void => {
      const t = track as RemoteAudioTrack
      if (t && t.kind === Track.Kind.Audio) detachAuxAudio(t)
    }
    next.on(RoomEvent.TrackSubscribed, onSub)
    next.on(RoomEvent.TrackUnsubscribed, onUnsub)
    installedDisposers.push(
      () => next.off(RoomEvent.TrackSubscribed, onSub),
      () => next.off(RoomEvent.TrackUnsubscribed, onUnsub),
    )
  }

  // Pick a store-specific subscription so we react when THIS room
  // changes. We don't know which store getRoom reads from, but
  // subscribing to BOTH is cheap (the function only runs reconcile()
  // which compares refs) and works regardless.
  const unsubLk = useLivekitStore.subscribe(reconcile)
  const unsubDm = useDmCallStore.subscribe(reconcile)
  reconcile()

  return () => {
    unsubLk()
    unsubDm()
    teardown()
  }
}
